import clsx from 'clsx';
import { useState } from 'react';
import { Button } from 'react-aria-components';

import { MaterialIcon } from '#/shared/components/MaterialIcon';
import { useFeatureFlag } from '#/shared/hooks/useFeatureFlag';
import { usePageStateStore } from '#/shared/stores/pageStateStore';
import { useTimesheetActionViewStore } from '#/shared/stores/timesheetActionViewStore';
import { useTimesheetStore } from '#/shared/stores/timesheetStore';
import { formatZonedDateTimeInLocalZone } from '#/shared/utils/dateTimeHelper';
import { focusRingClassName } from '#/shared/utils/domHelper';

import { ResetVerifiedTimesheetModal } from './ResetVerifiedTimesheetModal';

export function VerifiedTimesheetAlert() {
  const isVisible = true;
  const [isResetVerifiedTimesheetModalOpen, setIsResetVerifiedTimesheetModalOpen] = useState(false);
  const actionsContainerRef = usePageStateStore(state => state.actionsContainerRef);
  const setView = useTimesheetActionViewStore(state => state.setView);
  const timesheet = useTimesheetStore(state => state.timesheet);
  const verifiedBy = timesheet?.verifiedBy;

  // TODO: PPL-2240-verify-timesheet-action cleanup - Remove this feature flag usage
  const { isEnabled: isVerifyTimesheetEnabled } = useFeatureFlag('PPL-2240-verify-timesheet-action');

  const handleOnPressStartTimesheetApproval = () => {
    setView('approve');
    actionsContainerRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleOnPressResetVerification = () => {
    setIsResetVerifiedTimesheetModalOpen(true);
  };

  if (!isVisible || !isVerifyTimesheetEnabled || !verifiedBy) return null;

  return (
    <section className="flex flex-col p-md rounded-xs border-1 border-primary-tint bg-primary-surface-l1 mb-24">
      <div className="flex flex-col grow gap-16">
        <div className="flex items-center gap-xxs">
          <MaterialIcon iconName="check_circle" size="md" />
          <h3 className="text-16 leading-20 font-700 text-on-surface">Timesheet verified and pending approval</h3>
        </div>
        <div>
          <p className="text-16 leading-20 font-400 text-on-surface">Verified by {verifiedBy.name}</p>
          <p className="text-16 leading-20 font-400 text-on-surface">
            {verifiedBy.role}
            {verifiedBy.department && `, ${verifiedBy.department}`}
          </p>
          <p className="text-16 leading-20 font-400 text-on-surface">
            {formatZonedDateTimeInLocalZone(verifiedBy.verifiedAt, 'dd MMM yyyy HH:mm:ss')}
          </p>
        </div>

        {verifiedBy.notes && (
          <div>
            <p className="text-16 leading-20 font-400 text-on-surface">Verifier notes: {verifiedBy.notes}</p>
          </div>
        )}

        <div className="flex justify-between">
          <Button
            className={clsx(
              'text-left',
              'text-primary-on-surface-variant text-16 leading-20 font-600',
              'hover:text-primary-on-surface hover:cursor-pointer',
              `focus:text-primary-on-surface ${focusRingClassName}`,
            )}
            onPress={handleOnPressStartTimesheetApproval}
          >
            Start timesheet approval
          </Button>

          <Button
            className={clsx(
              'text-left',
              'text-error-on-surface-variant text-16 leading-20 font-600',
              'hover:text-error-on-surface hover:cursor-pointer',
              `focus:text-error-on-surface ${focusRingClassName}`,
            )}
            onPress={handleOnPressResetVerification}
          >
            Reset verification
          </Button>
        </div>
      </div>
      <ResetVerifiedTimesheetModal
        isOpen={isResetVerifiedTimesheetModalOpen}
        onClose={() => setIsResetVerifiedTimesheetModalOpen(false)}
      />
    </section>
  );
}
